import { useCallback, useEffect, useRef, useState } from 'react';
import {
  MicrophoneIcon,
  PhoneArrowUpRightIcon,
  PhoneXMarkIcon,
} from '@heroicons/react/24/solid';
import { MicrophoneIcon as MicrophoneOutlineIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import Avatar from '../../components/Avatar';
import { useVoiceStore } from '../../stores/voiceStore';
import { voiceService, type TransferableStaff } from './services/voiceService';
import { teardownPeer } from './webrtc';
import { twilioClient } from './twilio-client';
import { CallRecorder } from './recorder';
import { useSocketEvent } from '../../lib/websocket/hooks';
import { SOCKET_EVENTS, type CallStatusPayload } from '../../lib/websocket/events';

function formatDuration(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(s / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const seconds = s % 60;
  const pad = (n: number) => n.toString().padStart(2, '0');
  if (hours > 0) return `${hours}:${pad(minutes)}:${pad(seconds)}`;
  return `${pad(minutes)}:${pad(seconds)}`;
}

export default function InCallPanel() {
  const activeCall = useVoiceStore((s) => s.activeCall);
  const clearActive = useVoiceStore((s) => s.clearActive);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [muted, setMuted] = useState(false);
  const [ending, setEnding] = useState(false);
  const [duration, setDuration] = useState(0);

  // Transfer picker state
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const [staffList, setStaffList] = useState<TransferableStaff[] | null>(null);

  // Recording lifecycle. We start the recorder when both streams are available
  // and stop+upload exactly once when the call ends — whether the local user
  // clicks End or the remote peer hangs up (signalled via `call:status`).
  const recorderRef = useRef<CallRecorder | null>(null);
  const recordingCallIdRef = useRef<string | null>(null);
  const finalizedRef = useRef(false);

  // Bind remote stream to the audio element when it changes.
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    if (activeCall?.remoteStream) {
      el.srcObject = activeCall.remoteStream;
      el.play().catch(() => {
        /* autoplay policy — user already clicked Answer, so this should work */
      });
    } else {
      el.srcObject = null;
    }
  }, [activeCall?.remoteStream]);

  // ontrack on the peer connection: remoteStream may arrive after setActive.
  // Twilio path doesn't expose an RTCPeerConnection — the SDK pipes audio
  // through its own internal sink, so this effect is a no-op there.
  useEffect(() => {
    if (!activeCall || activeCall.transport !== 'webrtc') return;
    const pc = activeCall.pc;
    if (!pc) return;
    const handler = (event: RTCTrackEvent) => {
      const stream = event.streams[0];
      if (!stream) return;
      useVoiceStore.setState((state) => {
        if (!state.activeCall || state.activeCall.call.id !== activeCall.call.id) {
          return state;
        }
        return {
          ...state,
          activeCall: { ...state.activeCall, remoteStream: stream },
        };
      });
    };
    pc.addEventListener('track', handler);
    return () => {
      pc.removeEventListener('track', handler);
    };
  }, [activeCall]);

  // Live duration timer.
  useEffect(() => {
    if (!activeCall) {
      setDuration(0);
      return;
    }
    const start = new Date(activeCall.answeredAt).getTime();
    const tick = () => setDuration((Date.now() - start) / 1000);
    tick();
    const interval = window.setInterval(tick, 500);
    return () => window.clearInterval(interval);
  }, [activeCall]);

  // Stop the recorder and fire-and-forget the upload. Idempotent — safe to
  // call from both the local End click and the remote hangup signal.
  const finalizeRecording = useCallback(async (): Promise<void> => {
    if (finalizedRef.current) return;
    finalizedRef.current = true;

    const recorder = recorderRef.current;
    const callId = recordingCallIdRef.current;
    recorderRef.current = null;
    recordingCallIdRef.current = null;

    if (!recorder || !callId) return;

    try {
      const result = await recorder.stop();
      if (!result) return;

      // Fire-and-forget — the upload must not block the UI's End flow.
      void voiceService
        .uploadCallRecording(
          callId,
          {
            mixed: result.mixed,
            staff: result.staff,
            customer: result.customer,
          },
          result.durationMs
        )
        .catch((err: unknown) => {
          // eslint-disable-next-line no-console
          console.warn('[voice] failed to upload call recording:', err);
        });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[voice] failed to stop recorder cleanly:', err);
    }
  }, []);

  // Handle peer-connection failure (WebRTC) or remote disconnect (Twilio) →
  // auto-end. The Twilio SDK fires `disconnect` on the Call object when
  // either side hangs up; webrtc relies on connectionState transitions.
  useEffect(() => {
    if (!activeCall) return;

    if (activeCall.transport === 'webrtc') {
      const pc = activeCall.pc;
      if (!pc) return;
      const handler = () => {
        if (pc.connectionState === 'failed' || pc.connectionState === 'closed') {
          void finalizeRecording();
          teardownPeer(pc, activeCall.localStream ?? null);
          useVoiceStore.getState().clearActive();
        }
      };
      pc.addEventListener('connectionstatechange', handler);
      return () => {
        pc.removeEventListener('connectionstatechange', handler);
      };
    }

    // Twilio: listen for SDK-side disconnect / error.
    const call = activeCall.twilioCall;
    if (!call) return;
    const onDisconnect = () => {
      useVoiceStore.getState().clearActive();
    };
    const onError = (err: unknown) => {
      console.warn('[voice] twilio call error:', err);
      useVoiceStore.getState().clearActive();
    };
    call.on('disconnect', onDisconnect);
    call.on('error', onError);
    return () => {
      call.off('disconnect', onDisconnect);
      call.off('error', onError);
    };
  }, [activeCall, finalizeRecording]);

  // Start the recorder once we have a local mic and a remote stream. We can't
  // start any earlier — `createMediaStreamSource` throws on streams without
  // audio tracks. The remoteStream typically arrives via `ontrack` after the
  // initial setActive, so the effect re-runs when it appears.
  //
  // Skip entirely for Twilio: that path records server-side via TwiML's
  // `record="record-from-answer-dual"` parameter, and we don't have access
  // to the underlying MediaStreams anyway (the SDK manages them internally).
  useEffect(() => {
    if (!activeCall || activeCall.transport !== 'webrtc') return;
    const callId = activeCall.call.id;
    const { localStream, remoteStream } = activeCall;
    if (!localStream || !remoteStream) return;
    if (recordingCallIdRef.current === callId) return; // already started
    if (finalizedRef.current) return; // already torn down for this call

    const recorder = new CallRecorder();
    try {
      recorder.start(localStream, remoteStream);
      recorderRef.current = recorder;
      recordingCallIdRef.current = callId;
    } catch (err) {
      // Browser doesn't support MediaRecorder, or the streams have no audio
      // tracks. The call still works — we just won't have a recording.
      // eslint-disable-next-line no-console
      console.warn('[voice] call recording unavailable:', err);
    }
  }, [activeCall]);

  // Reset finalize-once guard when a NEW call becomes active (rare — End
  // clears activeCall first — but defensive against quick re-answer flows).
  useEffect(() => {
    if (!activeCall) return;
    finalizedRef.current = false;
  }, [activeCall?.call.id]);

  // Customer hangup / server-side terminate signal. Backend emits
  // `call:status` with `completed` or `missed` to the conversation room.
  const onCallStatus = useCallback(
    (payload: CallStatusPayload) => {
      if (!activeCall) return;
      if (payload.callId !== activeCall.call.id) return;
      if (payload.status !== 'completed' && payload.status !== 'missed') return;

      void finalizeRecording();
      if (activeCall.transport === 'webrtc') {
        teardownPeer(activeCall.pc ?? null, activeCall.localStream ?? null);
      } else {
        // Twilio: the SDK Call may already be disconnected (this status
        // came from the server, which heard about the hangup via Twilio's
        // status callback). `endCall` is idempotent.
        twilioClient.endCall();
      }
      useVoiceStore.getState().clearActive();
    },
    [activeCall, finalizeRecording]
  );
  useSocketEvent(SOCKET_EVENTS.CALL_STATUS, onCallStatus);

  // All hooks MUST be above the early-return on line below — otherwise the
  // first time `activeCall` flips from null to non-null React sees a
  // different number of hook calls between renders and crashes with #310.
  const handleOpenTransfer = useCallback(() => {
    setTransferOpen((open) => !open);
    if (staffList === null) {
      voiceService
        .listTransferableStaff()
        .then(setStaffList)
        .catch(() => setStaffList([]));
    }
  }, [staffList]);

  if (!activeCall) return null;

  const handleToggleMute = () => {
    const next = !muted;
    if (activeCall.transport === 'twilio') {
      twilioClient.setMuted(next);
      setMuted(next);
      return;
    }
    const tracks = activeCall.localStream?.getAudioTracks() ?? [];
    if (tracks.length === 0) return;
    for (const t of tracks) t.enabled = !next;
    setMuted(next);
  };

  const handleTransfer = async (toStaff: TransferableStaff) => {
    if (!activeCall || transferring) return;
    setTransferring(true);
    const callId = activeCall.call.id;
    // Stop recorder + upload BEFORE we tear down the local streams (same
    // pattern as End). Don't block the UI on the upload completing.
    // Twilio path has no client-side recorder — `finalizeRecording` is a
    // safe no-op there.
    await finalizeRecording();
    try {
      await voiceService.transferCall(callId, toStaff.id);
      toast.success(`Transferred to ${toStaff.name}`);
    } catch {
      toast.error('Failed to transfer call');
    } finally {
      if (activeCall.transport === 'webrtc') {
        teardownPeer(activeCall.pc ?? null, activeCall.localStream ?? null);
      } else {
        twilioClient.endCall();
      }
      clearActive();
      setTransferring(false);
      setTransferOpen(false);
    }
  };

  const handleEnd = async () => {
    if (ending) return;
    setEnding(true);
    const callId = activeCall.call.id;
    // Stop the recorder + fire-and-forget upload BEFORE we tear down the
    // peer/streams. Don't await the upload — keep the UI responsive.
    await finalizeRecording();

    if (activeCall.transport === 'twilio') {
      // Twilio path: hang up the SDK call. The backend learns via Twilio's
      // status callback — no need to POST /end. (Calling /end here would be
      // harmless but redundant; we skip it to keep the responsibility line
      // clear: server hears about Twilio hangups from Twilio.)
      twilioClient.endCall();
      clearActive();
      setEnding(false);
      return;
    }

    try {
      await voiceService.endCall(callId);
    } catch {
      toast.error('Failed to end call cleanly');
    } finally {
      teardownPeer(activeCall.pc ?? null, activeCall.localStream ?? null);
      clearActive();
      setEnding(false);
    }
  };

  const customerName = activeCall.call.customerName || 'Unknown caller';
  // Staff-initiated outbound calls use the sentinel id 'outbound' until the
  // backend webhook creates the real Call row. We don't reconcile that id
  // back to the client in V1.5, so cold-transfer (which requires the row id)
  // is hidden for outbound — staff can end and the customer can be re-dialed.
  const canTransfer = activeCall.call.id !== 'outbound';

  return (
    <div className="pointer-events-none fixed bottom-4 right-4 z-50 w-80 max-w-[calc(100vw-2rem)]">
      {/* Always-mounted audio sink for the remote stream */}
      <audio ref={audioRef} autoPlay playsInline />

      <div className="pointer-events-auto overflow-hidden rounded-card border border-line bg-surface shadow-l">
        <div className="flex items-center justify-between border-b border-line px-4 py-2">
          <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-brand">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-pulse rounded-full bg-signal opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-signal" />
            </span>
            On call
          </div>
          <span className="font-mono text-xs tabular-nums text-ink-2">
            {formatDuration(duration)}
          </span>
        </div>

        <div className="flex items-center gap-3 px-4 py-3">
          <Avatar name={customerName} size="lg" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-ink">{customerName}</p>
            <p className="truncate font-mono text-xs text-ink-2">
              {activeCall.call.customerPhone}
            </p>
          </div>
        </div>

        {transferOpen && (
          <div className="border-t border-line bg-surface-2 px-4 py-3">
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-ink-3">
              Transfer to
            </p>
            {staffList === null ? (
              <p className="text-xs text-ink-3">Loading…</p>
            ) : staffList.length === 0 ? (
              <p className="text-xs text-ink-3">No other staff available.</p>
            ) : (
              <ul className="max-h-40 space-y-1 overflow-y-auto">
                {staffList.map((s) => (
                  <li key={s.id}>
                    <button
                      type="button"
                      disabled={transferring}
                      onClick={() => handleTransfer(s)}
                      className="flex w-full items-center justify-between rounded-card px-2 py-1.5 text-left text-sm text-ink transition-colors hover:bg-black/[0.04] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <span className="truncate">{s.name}</span>
                      {s.online ? (
                        <span className="ml-2 inline-block h-2 w-2 flex-shrink-0 rounded-full bg-signal" title="Online" />
                      ) : (
                        <span className="ml-2 inline-block h-2 w-2 flex-shrink-0 rounded-full bg-ink-3" title="Offline" />
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <p className="mt-2 text-[10px] leading-snug text-ink-3">
              Cold transfer: this call ends, the conversation moves to the
              chosen staff, and the customer gets a follow-up message.
            </p>
          </div>
        )}

        <div className="flex gap-2 border-t border-line bg-surface-2 px-4 py-3">
          <button
            onClick={handleToggleMute}
            type="button"
            aria-pressed={muted}
            className={
              muted
                ? 'inline-flex flex-1 items-center justify-center gap-1.5 rounded-pill bg-warning/15 px-3 py-2 text-sm font-medium text-warning ring-1 ring-warning/40 transition-colors hover:bg-warning/25'
                : 'inline-flex flex-1 items-center justify-center gap-1.5 rounded-pill bg-surface border border-line px-3 py-2 text-sm font-medium text-ink transition-colors hover:bg-black/[0.04]'
            }
          >
            {muted ? (
              <MicrophoneOutlineIcon className="h-4 w-4" />
            ) : (
              <MicrophoneIcon className="h-4 w-4" />
            )}
            {muted ? 'Muted' : 'Mute'}
          </button>
          {canTransfer && (
            <button
              onClick={handleOpenTransfer}
              type="button"
              disabled={transferring || ending}
              aria-pressed={transferOpen}
              className={
                transferOpen
                  ? 'inline-flex flex-1 items-center justify-center gap-1.5 rounded-pill bg-brand/15 px-3 py-2 text-sm font-medium text-brand ring-1 ring-brand/40 transition-colors hover:bg-brand/25'
                  : 'inline-flex flex-1 items-center justify-center gap-1.5 rounded-pill bg-surface border border-line px-3 py-2 text-sm font-medium text-ink transition-colors hover:bg-black/[0.04] disabled:cursor-not-allowed disabled:opacity-50'
              }
            >
              <PhoneArrowUpRightIcon className="h-4 w-4" />
              {transferring ? 'Transferring…' : 'Transfer'}
            </button>
          )}
          <button
            onClick={handleEnd}
            disabled={ending || transferring}
            type="button"
            className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-pill bg-danger px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-danger/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <PhoneXMarkIcon className="h-4 w-4" />
            {ending ? 'Ending...' : 'End'}
          </button>
        </div>
      </div>
    </div>
  );
}
